import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Clapperboard, Film, Images, LayoutGrid, Scissors } from 'lucide-react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { formatDate } from '../../lib/utils';

type GalleryKind = 'all' | 'submission' | 'ai_edit' | 'animation_studio';

type GalleryVideo = {
  kind: 'submission' | 'ai_edit' | 'animation_studio';
  id: string;
  title: string;
  subtitle: string;
  videoUrl: string | null;
  createdAt: string;
  href: string;
  status?: string;
};

type GalleryResponse = {
  submissionVideos: GalleryVideo[];
  aiEdits: GalleryVideo[];
  animationStudio: GalleryVideo[];
};

function VideoThumb({ url, label }: { url: string | null; label: string }) {
  if (url && (/^https?:\/\//i.test(url) || url.startsWith('/'))) {
    return (
      <video
        src={url}
        className="w-full h-full object-cover bg-black"
        muted
        playsInline
        preload="metadata"
        aria-label={label}
      />
    );
  }
  return (
    <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-glass-3 text-glass-text-3 p-4 text-center">
      <Film className="w-10 h-10 opacity-50" aria-hidden />
      <span className="text-xs">{url ? 'Preview not available' : 'No preview yet'}</span>
    </div>
  );
}

const tabs: { id: GalleryKind; label: string; icon: typeof Images }[] = [
  { id: 'all', label: 'All', icon: LayoutGrid },
  { id: 'submission', label: 'Submissions', icon: Film },
  { id: 'ai_edit', label: 'AI video edits', icon: Scissors },
  { id: 'animation_studio', label: 'Animation studio', icon: Clapperboard },
];

export default function AgencyGallery() {
  const [data, setData] = useState<GalleryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<GalleryKind>('all');

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: d } = await api.get<GalleryResponse>('/agency/gallery');
      setData(d);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Could not load gallery');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const merged = useMemo(() => {
    if (!data) return [];
    const all: GalleryVideo[] = [
      ...data.submissionVideos,
      ...data.aiEdits,
      ...data.animationStudio,
    ];
    all.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    return all;
  }, [data]);

  const visible = useMemo(() => {
    if (!data) return [];
    if (tab === 'all') return merged;
    if (tab === 'submission') return data.submissionVideos;
    if (tab === 'ai_edit') return data.aiEdits;
    return data.animationStudio;
  }, [data, merged, tab]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-[2rem] font-semibold tracking-tight text-glass-text flex items-center gap-2">
          <Images className="w-8 h-8 text-accent2" />
          Gallery
        </h1>
        <p className="text-glass-text-2 mt-1 max-w-3xl">
          Campaign submission videos, completed AI video edits with a playable file, and animation studio MP4
          exports—filter by source or browse everything in one place.
        </p>
      </div>

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-medium transition-all ${
                active
                  ? 'border-accent2 bg-accent2/15 text-glass-text ring-1 ring-accent2/30'
                  : 'border-glass bg-glass-2 text-glass-text-2 hover:bg-glass-3 hover:text-glass-text'
              }`}
            >
              <Icon className="w-4 h-4 shrink-0" aria-hidden />
              {t.label}
            </button>
          );
        })}
      </div>

      {error && (
        <p className="text-red-400 text-sm border border-red-500/30 bg-red-500/10 rounded-lg px-3 py-2">{error}</p>
      )}

      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle className="text-glass-text">
            {tab === 'all' && 'All videos'}
            {tab === 'submission' && 'Submission videos'}
            {tab === 'ai_edit' && 'AI video edits'}
            {tab === 'animation_studio' && 'Animation studio'}
          </CardTitle>
          <span className="text-sm text-glass-text-3">{visible.length} item{visible.length !== 1 ? 's' : ''}</span>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-glass-text-2 py-12 text-center">Loading…</p>
          ) : visible.length === 0 ? (
            <div className="py-12 text-center space-y-3">
              <p className="text-glass-text-2">Nothing here yet.</p>
              <div className="flex flex-wrap justify-center gap-2 text-sm">
                <Link to="/agency/submissions" className="text-accent2 hover:underline">
                  Submissions
                </Link>
                <span className="text-glass-text-3">·</span>
                <Link to="/agency/ai-video-edits" className="text-accent2 hover:underline">
                  AI video edits
                </Link>
                <span className="text-glass-text-3">·</span>
                <Link to="/agency/animation-studio" className="text-accent2 hover:underline">
                  Animation studio
                </Link>
              </div>
            </div>
          ) : (
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {visible.map((item) => (
                <li key={`${item.kind}-${item.id}`}>
                  <Link
                    to={item.href}
                    className="group block rounded-xl border border-glass bg-glass-2 overflow-hidden hover:border-accent2/40 hover:bg-glass-3 transition-all h-full flex flex-col"
                  >
                    <div className="aspect-video bg-glass-3 relative shrink-0">
                      <VideoThumb url={item.videoUrl} label={item.title} />
                      <div className="absolute top-2 left-2">
                        <span className="text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded-md bg-black/55 text-white/95 backdrop-blur-sm">
                          {item.kind === 'submission'
                            ? 'Submission'
                            : item.kind === 'ai_edit'
                              ? 'AI edit'
                              : 'Animation'}
                        </span>
                      </div>
                    </div>
                    <div className="p-3 min-w-0 flex-1 flex flex-col">
                      <p className="font-medium text-glass-text truncate group-hover:text-accent2 transition-colors">
                        {item.title}
                      </p>
                      <p className="text-xs text-glass-text-3 mt-0.5 line-clamp-2">{item.subtitle}</p>
                      <p className="text-xs text-glass-text-3 mt-auto pt-2">{formatDate(item.createdAt)}</p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
